import { useState, Link, motion, AnimatePresence, Button, Card, Mail, HelpCircle, ChevronDown, LOGO_URL } from "@/pages/shared";
import { GettingStartedWalkthrough } from "../components/GettingStartedWalkthrough";
import { CLAIMS } from "@/config/claims";

export const HelpPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      question: "How do I generate content?",
      answer: "1. Go to Dashboard → Content\n2. Select content type (Blog, Social Media, Ad Copy, Twitter Thread, Instagram Caption, or TikTok Script)\n3. Enter your topic\n4. Choose tone and length\n5. Click 'Generate' and our AI will create content for you!\n\nTip: set up your Brand Kit in Settings (business name, tone, audience, colours) and every generation comes out on-brand automatically."
    },
    {
      question: "How does auto-posting to social media work?",
      answer: "Creatara can publish your posts FOR you — no copy-pasting needed:\n1. On the Content page, click 'Schedule' on any post\n2. Pick a date and time (shown in your own timezone)\n3. Tick the platforms to auto-post to: X (Twitter), LinkedIn, Facebook Page, and Instagram (Instagram needs an image attached)\n4. At the scheduled time, your post publishes automatically\n\nIf you don't tick any platform, we'll email you a one-click-share reminder at that time instead."
    },
    {
      question: "How do I connect my own LinkedIn and X accounts?",
      answer: "Go to Dashboard → Settings and look for the 'Your LinkedIn account' and 'Your X account' cards:\n1. Click 'Connect LinkedIn' or 'Connect X'\n2. Approve the authorization on LinkedIn/X (takes ~30 seconds)\n3. Done — scheduled posts now publish directly to YOUR feed, as you\n\nNote: LinkedIn connections expire after ~60 days (a LinkedIn rule) — we'll email you a reminder before that happens so you can reconnect in one click. X connections don't expire."
    },
    {
      question: "Can I add images to my posts?",
      answer: "Yes! Open any post with 'View Full':\n• Click 'Generate AI Image' to create a unique image that matches your post (and your Brand Kit style)\n• Or click 'Upload Image' to use your own (max 10MB)\n\nAn image is required for Instagram auto-posting, and it doubles as the video thumbnail on X."
    },
    {
      question: "Can I create videos?",
      answer: "Yes — Pro and Enterprise plans can turn any post into a short video clip:\n1. Open a post with 'View Full'\n2. In the Video Clip section, choose TikTok/Reel (9:16) or YouTube (16:9) format\n3. Click 'Generate Video Clip' — Sora 2 renders a 4-second clip in 1–3 minutes\n4. Download it, or schedule the post to X and the video attaches automatically as native media"
    },
    {
      question: "What is the Complete Post Bundle?",
      answer: "One click builds a finished, ready-to-publish post:\n• Polished caption\n• AI image\n• Relevant hashtags\n• Alt text (accessibility)\n• A 'first comment' to pin for extra reach\n\nOpen any social post with 'View Full' and click 'Build Complete Post'. Copy the entire bundle in one click."
    },
    {
      question: "What does the 💰 monetization score mean?",
      answer: "Every post gets an AI-predicted revenue-potential score out of 100, based on calls-to-action, urgency, benefit language, length, and hashtags. Aim for 75+. Use 'Optimize for platform' or regenerate with a bolder tone to improve a low score."
    },
    {
      question: "How do I use content templates?",
      answer: "Two kinds of templates:\n• Your templates: Dashboard → Templates — save your favourite settings, use {topic} as a placeholder, then pick the template when generating\n• Featured Templates: on the Content page — 8 pre-built revenue-optimised recipes (Podcast Repurposing with one-click reels, Affiliate Reviews, Lead Magnets, and more)"
    },
    {
      question: "How does the referral program work?",
      answer: `Earn ${CLAIMS.referral.creditsPerSignup} free content credits for each friend who signs up:\n1. Go to Dashboard → Referrals\n2. Copy your unique referral link\n3. Share with friends\n4. When they sign up, you both benefit!\n\n${CLAIMS.referral.mechanics}`
    },
    {
      question: "How do affiliate links work?",
      answer: "Track clicks on your promotional links:\n1. Go to Dashboard → Affiliates\n2. Create a link with your destination URL\n3. Add a custom slug (e.g., 'my-product')\n4. Share your link and track clicks in real-time\n\nUTM tracking parameters are added automatically so your analytics show exactly where clicks came from."
    },
    {
      question: "How do I capture leads?",
      answer: "Create lead capture forms:\n1. Go to Dashboard → Leads\n2. Create a new form with your desired fields\n3. Share the form URL or embed it\n4. Receive email notifications for new submissions"
    },
    {
      question: "Can I create flyers and price lists?",
      answer: "Yes — Dashboard → Flyers generates professional PDF flyers and menus for your services in one click. Perfect for printing, WhatsApp, or attaching to emails."
    },
    {
      question: "What's included in each plan?",
      answer: "• Free: 10 AI generations/month + referral bonuses, content calendar, custom affiliate slugs\n• Pro (£29/mo): 100 generations/month, 30 AI images, 5 video clips, priority support\n• Enterprise (£99/mo): Unlimited generations, white-label branding + agency workspaces, API access, dedicated support"
    },
    {
      question: "How do I upgrade my plan?",
      answer: "1. Go to Dashboard → Settings\n2. Click 'Upgrade' on your desired plan\n3. Complete payment through Stripe\n4. Your plan updates instantly!"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <nav className="fixed top-0 w-full z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Creatara - Help Center for Content Creator Tools" className="w-10 h-10 rounded-lg" />
            <span className="text-xl font-bold">Creatara</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/contact" className="text-muted-foreground hover:text-foreground">Contact</Link>
            <Link to="/"><Button variant="ghost">Home</Button></Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="text-4xl font-bold mb-4 text-center">Help Center</h1>
            <p className="text-muted-foreground text-center mb-8">Everything you need to know about Creatara</p>

            <GettingStartedWalkthrough />

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <Card key={index} className="glass overflow-hidden">
                  <button
                    className="w-full p-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="font-medium flex items-center gap-2">
                      <HelpCircle className="w-5 h-5 text-violet-500" />
                      {faq.question}
                    </span>
                    <ChevronDown className={`w-5 h-5 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="p-4 pt-0 text-muted-foreground whitespace-pre-line border-t border-white/10">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-muted-foreground mb-4">Still have questions?</p>
              <Link to="/contact">
                <Button className="btn-primary-glow bg-violet-600 hover:bg-violet-700 rounded-full">
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};
